import React, { useEffect, useRef, useState } from "react";
import { slipPdfToJpeg } from "@/lib/pdfToJpeg";
import { rupiah } from "@/lib/format";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Loader2, Download, Share2, MessageCircle } from "lucide-react";
import { toast } from "sonner";

// Pratinjau slip JPEG per karyawan + kirim WhatsApp.
export default function SlipShare({ open, onClose, detail, period, companyName = "CPS PAYROLL", nomorWa = "" }) {
  const [loading, setLoading] = useState(false);
  const [dataUrl, setDataUrl] = useState(null);
  const blobRef = useRef(null);

  const nama = detail?.employee_nama || "Karyawan";
  const digits = (() => {
    let d = String(nomorWa || "").replace(/[^0-9]/g, "");
    if (!d) return "";
    if (d.startsWith("0")) d = "62" + d.slice(1);
    else if (!d.startsWith("62")) d = "62" + d;
    return d;
  })();
  const fileName = `slip_${nama}_${period?.nama || ""}`.replace(/\s+/g, "_") + ".jpg";
  const waText = `*Slip Gaji ${companyName}*\n${nama}\nPeriode: ${period?.nama || "-"}\nGaji Bersih (THP): ${rupiah(detail?.gaji_bersih || 0)}\n\nTerlampir slip gaji Anda. Terima kasih.`;

  useEffect(() => {
    if (!open || !detail) return;
    let cancelled = false;
    setLoading(true); setDataUrl(null); blobRef.current = null;
    slipPdfToJpeg(`/payroll/details/${detail.id}/slip.pdf`)
      .then(({ blob, dataUrl }) => { if (!cancelled) { blobRef.current = blob; setDataUrl(dataUrl); } })
      .catch(() => { if (!cancelled) toast.error("Gagal membuat gambar slip"); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, detail]);

  const downloadJpeg = () => {
    if (!blobRef.current) return;
    const u = URL.createObjectURL(blobRef.current);
    const a = document.createElement("a"); a.href = u; a.download = fileName; a.click();
    URL.revokeObjectURL(u);
  };

  const shareWA = async () => {
    // 1) Jika nomor WA pegawai tersedia → buka chat LANGSUNG ke nomornya + unduh gambar utk dilampirkan
    if (digits) {
      downloadJpeg();
      toast.info("Gambar slip diunduh. WhatsApp terbuka ke nomor pegawai — lampirkan gambar tsb.", { duration: 7000 });
      window.open(`https://wa.me/${digits}?text=${encodeURIComponent(waText)}`, "_blank", "noopener");
      return;
    }
    const file = blobRef.current ? new File([blobRef.current], fileName, { type: "image/jpeg" }) : null;
    // 2) Web Share API dengan file (HP: langsung pilih WhatsApp + gambar terlampir)
    if (file && navigator.canShare && navigator.canShare({ files: [file] })) {
      try {
        await navigator.share({ files: [file], text: waText, title: `Slip Gaji ${nama}` });
        return;
      } catch (e) { if (e?.name === "AbortError") return; }
    }
    // 3) Fallback: unduh JPEG lalu buka WhatsApp dengan teks (user lampirkan gambar)
    downloadJpeg();
    toast.info("Gambar slip diunduh. WhatsApp akan terbuka — lampirkan gambar yang baru diunduh.", { duration: 7000 });
    window.open(`https://wa.me/?text=${encodeURIComponent(waText)}`, "_blank", "noopener");
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg max-h-[94vh] overflow-hidden flex flex-col" data-testid="slip-share-dialog">
        <DialogHeader><DialogTitle>Slip Gaji — {nama}</DialogTitle>
          <DialogDescription className="sr-only">Pratinjau slip gaji format JPEG siap dikirim ke WhatsApp.</DialogDescription>
        </DialogHeader>
        {digits && <div className="text-xs text-emerald-700 -mt-1" data-testid="slip-share-target">Kirim langsung ke nomor: +{digits}</div>}
        <div className="flex-1 min-h-[52vh] max-h-[64vh] overflow-auto scroll-thin rounded-lg border border-slate-200 bg-slate-100 p-2">
          {loading ? (
            <div className="flex h-[52vh] items-center justify-center text-slate-400"><Loader2 className="h-6 w-6 animate-spin mr-2" /> Menyiapkan gambar slip…</div>
          ) : dataUrl ? (
            <img src={dataUrl} alt={`Slip ${nama}`} className="mx-auto w-full rounded shadow-sm bg-white" data-testid="slip-share-img" />
          ) : (
            <div className="flex h-[52vh] items-center justify-center text-slate-400">Gagal memuat gambar.</div>
          )}
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={downloadJpeg} disabled={!dataUrl} data-testid="slip-share-download"><Download className="h-4 w-4 mr-1" /> Unduh JPEG</Button>
          <Button className="bg-[#25D366] hover:bg-[#1ebe5b] text-white" onClick={shareWA} disabled={!dataUrl} data-testid="slip-share-wa">
            {typeof navigator !== "undefined" && navigator.share ? <Share2 className="h-4 w-4 mr-1" /> : <MessageCircle className="h-4 w-4 mr-1" />} Kirim ke WhatsApp
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
